import React, { useCallback, useEffect, useRef, useState } from 'react';
import API from '../services/api';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../components/ui/Icon';
import EmptyState from '../components/ui/EmptyState';
import ErrorState from '../components/ui/ErrorState';
import { SkeletonCard } from '../components/ui/Skeleton';
import './MockTest.css';

// ── Constants ────────────────────────────────────────────────────────────────
// One selection colour for the whole screen (violet). Categories are told apart by glyph, not by a rainbow.
const CATEGORY_META = {
  quantitative: { icon: 'chart', label: 'Quantitative' },
  logical: { icon: 'skill', label: 'Logical' },
  verbal: { icon: 'book', label: 'Verbal' },
  programming: { icon: 'test', label: 'Programming' },
  sql: { icon: 'grid', label: 'SQL' },
};
const categoryMeta = (cat) => CATEGORY_META[cat] || { icon: 'list', label: cat };

// tone maps to the shared semantic chips: success / warning / danger are the only states these levels imply.
const DIFFICULTY_OPTIONS = [
  { value: 'mixed', label: 'Mixed', desc: 'All levels', tone: 'neutral' },
  { value: 'easy', label: 'Easy', desc: 'Basic level', tone: 'success' },
  { value: 'medium', label: 'Medium', desc: 'Intermediate', tone: 'warning' },
  { value: 'hard', label: 'Hard', desc: 'Advanced', tone: 'danger' },
];

const QUESTION_COUNTS = [5, 10, 15, 20, 30, 50];
const PERSONALIZED_COUNTS = [20, 30, 40, 50];

const FLOW_MODES = [
  { id: 'quick', icon: 'bolt', title: 'Quick Practice', desc: 'Pick topics and jump straight in' },
  { id: 'category', icon: 'grid', title: 'By Category', desc: 'Practice one full category at a time' },
  { id: 'difficulty', icon: 'flag', title: 'By Difficulty', desc: 'Challenge yourself by difficulty level' },
  { id: 'custom', icon: 'settings', title: 'Custom Build', desc: 'Pick topics, difficulty and count' },
  { id: 'personalized', icon: 'sparkle', title: 'Personalized Test', desc: 'Built from your mistakes and weak topics.' },
];

const flowTitle = (id) => (FLOW_MODES.find(m => m.id === id) || {}).title;

// ── Small building blocks ────────────────────────────────────────────────────
const TopicChip = ({ topic, selected, onToggle }) => (
  <button
    type="button"
    className={`pc-chip${selected ? ' is-selected' : ''}`}
    aria-pressed={selected}
    onClick={() => onToggle(topic.id)}
  >
    <span className="pc-chip-name">{topic.name}</span>
    <span className="pc-chip-count">{topic.question_count || 0}Q</span>
  </button>
);

const CountPills = ({ counts, value, onChange }) => (
  <div className="pc-pills" role="group" aria-label="Number of questions">
    {counts.map(c => (
      <button key={c} type="button" className={`pc-pill${value === c ? ' is-selected' : ''}`} aria-pressed={value === c} onClick={() => onChange(c)}>
        {c}
      </button>
    ))}
  </div>
);

const DifficultyPills = ({ options, value, onChange }) => (
  <div className="pc-pills" role="group" aria-label="Difficulty">
    {options.map(d => (
      <button
        key={d.value}
        type="button"
        className={`pc-pill pc-pill--${d.tone}${value === d.value ? ' is-selected' : ''}`}
        aria-pressed={value === d.value}
        onClick={() => onChange(d.value)}
      >
        {d.label}
      </button>
    ))}
  </div>
);

const RowLabel = ({ children }) => <div className="pc-label">{children}</div>;
const FormError = ({ message }) => (message ? <div className="pc-error" role="alert"><Icon name="warning" size={16} /> {message}</div> : null);

const StartButton = ({ children, ...props }) => (
  <button type="button" className="z-btn z-btn--primary z-btn--block pc-start" {...props}>{children}</button>
);

const TopicGroup = ({ cat, topics, selectedTopics, onToggle, onAll, onClear, showCount }) => {
  const meta = categoryMeta(cat);
  const selCnt = topics.filter(t => selectedTopics.includes(t.id)).length;
  const allSel = selCnt === topics.length;
  return (
    <div className="pc-group">
      <div className="pc-group-head">
        <div className="pc-group-label">
          <Icon name={meta.icon} size={18} /> {meta.label}
          {showCount && selCnt > 0 && <span className="z-chip z-chip--violet">{selCnt} selected</span>}
        </div>
        <div className="pc-group-actions">
          {(showCount || !allSel) && <button type="button" className="pc-text-btn" onClick={() => onAll(cat)}>All</button>}
          {(showCount || allSel) && <button type="button" className="pc-text-btn pc-text-btn--danger" onClick={() => onClear(cat)}>Clear</button>}
        </div>
      </div>
      <div className="pc-chips">
        {topics.map(t => <TopicChip key={t.id} topic={t} selected={selectedTopics.includes(t.id)} onToggle={onToggle} />)}
      </div>
    </div>
  );
};

// ── Main Component ───────────────────────────────────────────────────────────
const MockTestSetup = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const [topics, setTopics] = useState([]);
  const [history, setHistory] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(true);
  const [topicsError, setTopicsError] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');

  // ── Flow selection ─────────────────────────────────────────────────────────
  // The Practice Hub's Quick Play / Skill Practice / Timed Challenge cards
  // link here with a preferred mode already chosen — this only sets the
  // initial UI selection, the exact same one a student could reach by
  // clicking the card here themselves; no new start/submit path.
  const [activeFlow, setActiveFlow] = useState(state?.preselectFlow || null); // null | 'quick' | 'category' | 'difficulty' | 'custom' | 'personalized'

  // ── Shared config ──────────────────────────────────────────────────────────
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [difficulty, setDifficulty] = useState('mixed');
  const [questionCount, setQuestionCount] = useState(10);
  const [selectedCategory, setSelectedCategory] = useState(null);  // for 'category' flow
  const [selectedDiffOnly, setSelectedDiffOnly] = useState('easy'); // for 'difficulty' flow

  // ── Personalized flow / Journey ──────────────────────────────────────────────
  const [personalizedCount, setPersonalizedCount] = useState(20);
  const [personalizedStatus, setPersonalizedStatus] = useState(null); // { eligible, qualifying_tests_taken/required, active_mistakes, weak_topics, max_questions }
  const [journey, setJourney] = useState(null); // { eligible, current_stage, stages_completed, stages: [{n, label, state, completed_at}] }

  const panelRef = useRef(null);

  const loadTopics = useCallback(() => {
    setLoadingTopics(true);
    setTopicsError(null);
    API.get('/mock/topics')
      .then(res => setTopics(res.data))
      .catch(err => {
        console.error('Topics load error:', err);
        setTopicsError(err);
      })
      .finally(() => setLoadingTopics(false));
  }, []);

  useEffect(() => {
    loadTopics();

    API.get('/mock/history')
      .then(res => setHistory(res.data))
      .catch(err => console.error('History load error:', err));

    API.get('/mock/personalized/status')
      .then(res => setPersonalizedStatus(res.data))
      .catch(err => console.error('Personalized status load error:', err));

    API.get('/mock/personalized/journey')
      .then(res => setJourney(res.data))
      .catch(err => console.error('Personalized journey load error:', err));
  }, [loadTopics]);

  // After a mode is picked the configuration panel is below the fold on a phone — bring it into view.
  useEffect(() => {
    if (!activeFlow || !panelRef.current || typeof panelRef.current.scrollIntoView !== 'function') return;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    panelRef.current.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  }, [activeFlow]);

  const groupedTopics = topics.reduce((acc, t) => {
    if (!acc[t.category]) acc[t.category] = [];
    acc[t.category].push(t);
    return acc;
  }, {});

  const toggleTopic = (id) => {
    setSelectedTopics(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
    setError('');
  };

  const selectAllInCategory = (cat) => {
    const ids = (groupedTopics[cat] || []).map(t => t.id);
    setSelectedTopics(p => [...new Set([...p, ...ids])]);
  };

  const clearAllInCategory = (cat) => {
    const ids = (groupedTopics[cat] || []).map(t => t.id);
    setSelectedTopics(p => p.filter(id => !ids.includes(id)));
  };

  // ── Start mock test ────────────────────────────────────────────────────────
  const handleStart = async (overrides = {}) => {
    const topicIds = overrides.topic_ids || selectedTopics;
    const diff = overrides.difficulty || difficulty;
    const count = overrides.count || questionCount;

    if (topicIds.length === 0) { setError('Please select at least one topic'); return; }

    setStarting(true);
    setError('');
    try {
      const res = await API.post('/mock/start', {
        topic_ids: topicIds,
        difficulty: diff,
        question_count: count,
        session_type: activeFlow,
      });
      navigate('/student/mock/test', {
        state: {
          session_id: res.data.session_id,
          questions: res.data.questions,
          total: res.data.total,
          flowMode: activeFlow,
        },
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start. Try again.');
    } finally {
      setStarting(false);
    }
  };

  // ── Start personalized test (stage, when the journey is unlocked) ──────────
  const handleStartPersonalized = async () => {
    setStarting(true);
    setError('');
    try {
      const res = await API.post('/mock/personalized/start', {
        question_count: personalizedCount,
        // Only ever the server's OWN current_stage — never a client-chosen number (the server independently
        // re-validates this; it is never trusted as the source of unlock truth).
        stage: journey?.eligible ? journey.current_stage : undefined,
      });
      navigate('/student/mock/test', {
        state: {
          session_id: res.data.session_id,
          questions: res.data.questions,
          total: res.data.total,
          flowMode: 'personalized',
          journeyStage: res.data.journey_stage,
          journeyStageLabel: res.data.journey_stage_label,
        },
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start. Try again.');
    } finally {
      setStarting(false);
    }
  };

  const groupProps = { selectedTopics, onToggle: toggleTopic, onAll: selectAllInCategory, onClear: clearAllInCategory };
  const preparing = 'Preparing…';

  // ── Flow: Quick — select topics freely ────────────────────────────────────
  const renderQuickFlow = () => (
    <>
      <p className="pc-panel-sub">Select any topics you want to practice.</p>
      {Object.entries(groupedTopics).map(([cat, tlist]) => (
        <TopicGroup key={cat} cat={cat} topics={tlist} {...groupProps} />
      ))}

      <RowLabel>Number of questions</RowLabel>
      <CountPills counts={QUESTION_COUNTS} value={questionCount} onChange={setQuestionCount} />

      <FormError message={error} />
      <StartButton onClick={() => handleStart()} disabled={starting || selectedTopics.length === 0}>
        {starting ? preparing : <><Icon name="bolt" size={18} /> Start · {selectedTopics.length} topic{selectedTopics.length !== 1 ? 's' : ''} · {questionCount} questions</>}
      </StartButton>
    </>
  );

  // ── Flow: Category — one-tap per category ─────────────────────────────────
  const renderCategoryFlow = () => (
    <>
      <p className="pc-panel-sub">Pick a full category and start.</p>
      <div className="pc-cards">
        {Object.entries(groupedTopics).map(([cat, tlist]) => {
          const meta = categoryMeta(cat);
          const totalQ = tlist.reduce((s, t) => s + (parseInt(t.question_count) || 0), 0);
          const isActive = selectedCategory === cat;
          return (
            <button
              key={cat}
              type="button"
              className={`pc-card${isActive ? ' is-selected' : ''}`}
              aria-pressed={isActive}
              onClick={() => setSelectedCategory(isActive ? null : cat)}
            >
              <span className="pc-card-icon"><Icon name={meta.icon} size={22} /></span>
              <span className="pc-card-body">
                <span className="pc-card-title">{meta.label}</span>
                <span className="pc-card-desc">{tlist.length} topics · {totalQ} questions</span>
              </span>
              {isActive && <Icon name="check" size={20} className="pc-card-check" />}
            </button>
          );
        })}
      </div>

      {selectedCategory && (
        <>
          <RowLabel>Difficulty for {categoryMeta(selectedCategory).label}</RowLabel>
          <DifficultyPills options={DIFFICULTY_OPTIONS} value={difficulty} onChange={setDifficulty} />
          <RowLabel>Number of questions</RowLabel>
          <CountPills counts={QUESTION_COUNTS} value={questionCount} onChange={setQuestionCount} />
        </>
      )}

      <FormError message={error} />
      <StartButton
        disabled={!selectedCategory || starting}
        onClick={() => {
          const ids = (groupedTopics[selectedCategory] || []).map(t => t.id);
          handleStart({ topic_ids: ids, difficulty, count: questionCount });
        }}
      >
        {starting ? preparing
          : selectedCategory
            ? <>Start {categoryMeta(selectedCategory).label} · {questionCount} questions</>
            : 'Select a category above'}
      </StartButton>
    </>
  );

  // ── Flow: Difficulty — pick difficulty first, then topics ─────────────────
  const renderDifficultyFlow = () => (
    <>
      <p className="pc-panel-sub">Choose your challenge level first.</p>
      <div className="pc-cards pc-cards--3">
        {DIFFICULTY_OPTIONS.filter(d => d.value !== 'mixed').map(d => (
          <button
            key={d.value}
            type="button"
            className={`pc-card pc-card--stack pc-card--${d.tone}${selectedDiffOnly === d.value ? ' is-selected' : ''}`}
            aria-pressed={selectedDiffOnly === d.value}
            onClick={() => setSelectedDiffOnly(d.value)}
          >
            <span className="pc-card-title">{d.label}</span>
            <span className="pc-card-desc">{d.desc}</span>
          </button>
        ))}
      </div>

      <RowLabel>Topics (optional — leave all selected for full practice)</RowLabel>
      <div className="pc-chips">
        {topics.map(t => <TopicChip key={t.id} topic={t} selected={selectedTopics.includes(t.id)} onToggle={toggleTopic} />)}
      </div>
      <div className="pc-group-actions pc-group-actions--row">
        <button type="button" className="pc-text-btn" onClick={() => setSelectedTopics(topics.map(t => t.id))}>Select all topics</button>
        <button type="button" className="pc-text-btn pc-text-btn--danger" onClick={() => setSelectedTopics([])}>Clear all</button>
      </div>

      <RowLabel>Number of questions</RowLabel>
      <CountPills counts={QUESTION_COUNTS} value={questionCount} onChange={setQuestionCount} />

      <FormError message={error} />
      <StartButton
        disabled={starting}
        onClick={() => {
          const ids = selectedTopics.length > 0 ? selectedTopics : topics.map(t => t.id);
          handleStart({ topic_ids: ids, difficulty: selectedDiffOnly, count: questionCount });
        }}
      >
        {starting ? preparing : <>Start {selectedDiffOnly.charAt(0).toUpperCase() + selectedDiffOnly.slice(1)} · {questionCount} questions</>}
      </StartButton>
    </>
  );

  // ── Flow: Custom — full control ────────────────────────────────────────────
  const renderCustomFlow = () => (
    <>
      <p className="pc-panel-sub">Full control over every parameter.</p>
      {Object.entries(groupedTopics).map(([cat, tlist]) => (
        <TopicGroup key={cat} cat={cat} topics={tlist} showCount {...groupProps} />
      ))}

      <RowLabel>Difficulty level</RowLabel>
      <DifficultyPills options={DIFFICULTY_OPTIONS} value={difficulty} onChange={setDifficulty} />

      <RowLabel>Number of questions</RowLabel>
      <CountPills counts={QUESTION_COUNTS} value={questionCount} onChange={setQuestionCount} />

      <dl className="pc-summary" aria-label="Test summary">
        <div><dt>Topics</dt><dd>{selectedTopics.length}</dd></div>
        <div><dt>Questions</dt><dd>{questionCount}</dd></div>
        <div><dt>Difficulty</dt><dd className="pc-cap">{difficulty}</dd></div>
        <div><dt>Est. time</dt><dd>~{Math.ceil(questionCount * 1.5)}m</dd></div>
      </dl>

      <FormError message={error} />
      <StartButton onClick={() => handleStart()} disabled={starting || selectedTopics.length === 0}>
        {starting ? preparing : <>Start custom test <Icon name="arrow-right" size={18} /></>}
      </StartButton>
    </>
  );

  // ── Flow: Personalized Journey — evidence-gated, then a sequence of stages ──────────────────────────────────────────
  // Unlock state and the stage sequence are entirely server-computed (mockTestController.getJourneyProgress) — this
  // panel only ever displays what the server already decided; it never invents a stage count of its own.
  const renderPersonalizedFlow = () => {
    const counts = PERSONALIZED_COUNTS.filter(c => c <= (personalizedStatus?.max_questions || 50));

    // Same "assume locked until the server says otherwise" default the mode-selector card already used, so this
    // panel never flashes an unlocked view for an ineligible (or not-yet-loaded) student.
    if (!personalizedStatus?.eligible) {
      const taken = personalizedStatus?.qualifying_tests_taken ?? 0;
      const required = personalizedStatus?.qualifying_tests_required ?? 10;
      const need = Math.max(0, required - taken);
      return (
        <div className="pc-journey-locked">
          <EmptyState
            icon="lock" art={false} tone="neutral" compact
            title="Your Personalized Journey is building"
            body={need > 0 ? `Complete ${need} more Aptitude or Diagnostic test${need === 1 ? '' : 's'} to unlock it.` : 'Almost there — your next real test unlocks it.'}
          />
          <div className="pc-journey-progress" role="img" aria-label={`${taken} of ${required} tests completed`}>
            {Array.from({ length: required }, (_, i) => (
              <span key={i} className={`pc-journey-dot${i < taken ? ' is-done' : ''}`} />
            ))}
          </div>
          <p className="pc-journey-count">{taken} / {required} completed</p>
        </div>
      );
    }

    return (
      <>
        <p className="pc-panel-sub">Focused revision on your weakest areas — a step-by-step journey that adapts as you go.</p>

        {journey?.stages?.length > 0 && (
          <>
            <RowLabel>Your journey so far</RowLabel>
            <div className="pc-journey-map">
              {journey.stages.map(s => (
                <div key={s.n} className={`pc-journey-node is-${s.state}`}>
                  <span className="pc-journey-node-icon">
                    <Icon name={s.state === 'completed' ? 'check' : 'sparkle'} size={16} />
                  </span>
                  <span className="pc-journey-node-n">Stage {s.n}</span>
                  <span className="pc-journey-node-label">{s.label}</span>
                </div>
              ))}
            </div>
          </>
        )}

        <div className="pc-based-on">
          <span className="pc-based-on-label">{journey ? `Stage ${journey.current_stage} — built for you, based on:` : 'Built for you, based on:'}</span>
          <span className="z-chip z-chip--warning"><Icon name="mistake" size={14} /> {personalizedStatus?.active_mistakes || 0} active mistakes</span>
          <span className="z-chip z-chip--violet"><Icon name="trend-down" size={14} /> {personalizedStatus?.weak_topics || 0} weak topics</span>
        </div>

        <RowLabel>Number of questions</RowLabel>
        <CountPills counts={counts} value={personalizedCount} onChange={setPersonalizedCount} />

        <FormError message={error} />
        <StartButton onClick={handleStartPersonalized} disabled={starting}>
          {starting ? preparing : <><Icon name="sparkle" size={18} /> Start {journey ? `Stage ${journey.current_stage}` : 'Personalized Test'} · {personalizedCount} questions</>}
        </StartButton>
      </>
    );
  };

  const renderActiveFlow = () => {
    switch (activeFlow) {
      case 'quick': return renderQuickFlow();
      case 'category': return renderCategoryFlow();
      case 'difficulty': return renderDifficultyFlow();
      case 'custom': return renderCustomFlow();
      case 'personalized': return renderPersonalizedFlow();
      default: return null;
    }
  };

  const renderPanelBody = () => {
    // Personalized does not need the topic list; every other mode does.
    if (activeFlow !== 'personalized') {
      if (loadingTopics) return <div role="status" aria-label="Loading topics"><SkeletonCard lines={4} /></div>;
      if (topicsError) return <ErrorState error={topicsError} onRetry={loadTopics} compact />;
      if (topics.length === 0) {
        return <EmptyState icon="book" art={false} tone="neutral" compact title="No practice topics yet" body="Topics appear here once questions are added for your college." />;
      }
    }
    return renderActiveFlow();
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="z-page z-page--narrow pc-page">
      <p className="pc-intro">Practice on your own terms — choose a mode to get started. Nothing here counts toward an official assessment.</p>

      {/* ── Mode selector ── */}
      <section className="pc-section" aria-labelledby="pc-modes-title">
        <h2 id="pc-modes-title" className="pc-section-title">Choose a practice mode</h2>
        <div className="pc-cards">
          {FLOW_MODES.map(m => {
            const isPersonalized = m.id === 'personalized';
            const locked = isPersonalized && personalizedStatus && !personalizedStatus.eligible;
            const active = activeFlow === m.id;
            return (
              <button
                key={m.id}
                type="button"
                className={`pc-card pc-mode${active ? ' is-selected' : ''}${locked ? ' is-locked' : ''}`}
                aria-pressed={active}
                onClick={() => {
                  setActiveFlow(active ? null : m.id);
                  setError('');
                  // Auto-select all topics for difficulty flow
                  if (m.id === 'difficulty' && selectedTopics.length === 0) {
                    setSelectedTopics(topics.map(t => t.id));
                  }
                }}
              >
                <span className="pc-card-icon"><Icon name={locked ? 'lock' : m.icon} size={22} /></span>
                <span className="pc-card-body">
                  <span className="pc-card-title">
                    {m.title}
                    {isPersonalized && !locked && <span className="z-chip z-chip--violet pc-reco">Recommended</span>}
                  </span>
                  <span className="pc-card-desc">{m.desc}</span>
                  {isPersonalized && personalizedStatus?.eligible && (
                    <span className="pc-card-hint">
                      {personalizedStatus.active_mistakes || 0} active mistakes · {personalizedStatus.weak_topics || 0} weak topics
                    </span>
                  )}
                  {locked && (
                    <span className="pc-card-hint">
                      {personalizedStatus ? `${personalizedStatus.qualifying_tests_taken}/${personalizedStatus.qualifying_tests_required} tests to unlock` : 'Complete more tests to unlock'}
                    </span>
                  )}
                </span>
                <Icon name={active ? 'chevron-up' : 'chevron-down'} size={18} className="pc-card-chev" />
              </button>
            );
          })}
        </div>
      </section>

      {/* ── Active flow panel ── */}
      {activeFlow ? (
        <section className="z-surface pc-panel" ref={panelRef} aria-label={`${flowTitle(activeFlow)} setup`}>
          <h2 className="pc-panel-title">{flowTitle(activeFlow)}</h2>
          {renderPanelBody()}
        </section>
      ) : (
        <EmptyState icon="practice" art={false} tone="neutral" compact title="Select a practice mode above" body="Each mode gives you a different way to target your weak areas." />
      )}

      {/* ── Recent sessions ── */}
      <section className="pc-section" aria-labelledby="pc-history-title">
        <h2 id="pc-history-title" className="pc-section-title">Recent sessions</h2>
        {history.length === 0 ? (
          <EmptyState icon="list" art={false} tone="neutral" compact title="No sessions yet" body="Start your first test to see your progress here." />
        ) : (
          <ul className="pc-history">
            {history.slice(0, 8).map((h, i) => {
              const pct = parseFloat(h.percentage) || 0;
              const tone = pct >= 80 ? 'success' : pct >= 60 ? 'warning' : 'danger';
              return (
                <li key={i} className="pc-history-item">
                  <div className="pc-history-info">
                    <div className="pc-history-topic">{h.topic_name || 'Mixed Topics'}</div>
                    <div className="pc-history-meta">
                      {h.total_questions || 0}Q · {new Date(h.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                    </div>
                  </div>
                  <span className={`z-chip z-chip--${tone} pc-history-score`}>{pct}%</span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
};

export default MockTestSetup;
